import { Session, SignalEvent } from 'openvidu-browser-v2compatibility';
import { IChatAttachment, IChatMessage } from '../../shared/interfaces';
import { Roles, Signals } from '../../shared/enums';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';

import { Button } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import './ChatMessageView.css';

const MAX_ATTACHMENT_SIZE_BYTES = 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

interface AttachmentSignalPayload {
  kind: 'attachment';
  version: 1;
  id: string;
  origin: Roles;
  fileName: string;
  contentType: string;
  size: number;
  previewDataUrl: string;
}

export default function ChatMessageView(props: {
  setUnreadMessages: Function;
  isActiveChat: boolean;
  session: Session;
}) {
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [input, setInput] = useState('');

  function sendMessage() {
    if (!input.trim()) return;

    const messageFromInsured: IChatMessage = {
      read: false,
      kind: 'text',
      text: input,
      date: new Date(),
      origin: Roles.Insured,
    };

    props.session.signal({
      data: messageFromInsured.text,
      type: Signals.MessagingAnalyst,
    });

    setMessages((messages) => [...messages, messageFromInsured]);
    setInput('');
  }

  async function sendAttachment(file: File) {
    setAttachmentError('');

    const validationError = validateAttachment(file);
    if (validationError) {
      console.warn('[ChatAttachment] validation failed', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        validationError,
      });
      setAttachmentError(validationError);
      return;
    }

    if (!props.session.isSessionConnected()) {
      console.warn('[ChatAttachment] session is not connected', {
        fileName: file.name,
        sessionId: props.session.sessionId,
      });
      setAttachmentError('A sessao nao esta ativa.');
      return;
    }

    let previewDataUrlLength = 0;
    let payloadBytes = 0;

    try {
      const previewDataUrl = await readFileAsDataUrl(file);
      previewDataUrlLength = previewDataUrl.length;
      const attachment: IChatAttachment = {
        id: `${props.session.sessionId}-${Date.now()}`,
        fileName: file.name,
        contentType: getAttachmentContentType(file),
        size: file.size,
        previewDataUrl,
      };
      const payload: AttachmentSignalPayload = {
        kind: 'attachment',
        version: 1,
        origin: Roles.Insured,
        ...attachment,
      };
      const messageFromInsured: IChatMessage = {
        read: false,
        kind: 'attachment',
        date: new Date(),
        origin: Roles.Insured,
        attachment,
      };
      const payloadData = JSON.stringify(payload);
      payloadBytes = new TextEncoder().encode(payloadData).length;

      console.info('[ChatAttachment] sending attachment', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        previewDataUrlLength,
        payloadBytes,
        signalType: Signals.MessagingAnalyst,
        sessionId: props.session.sessionId,
        isConnected: props.session.isSessionConnected(),
      });

      await props.session.signal({
        data: payloadData,
        type: Signals.MessagingAnalyst,
      });

      console.info('[ChatAttachment] attachment signal sent', {
        attachmentId: attachment.id,
        payloadBytes,
      });
      setMessages((messages) => [...messages, messageFromInsured]);
    } catch (error) {
      console.error('[ChatAttachment] failed to send attachment', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        previewDataUrlLength,
        payloadBytes,
        sessionId: props.session.sessionId,
        isConnected: props.session.isSessionConnected(),
        error,
      });
      setAttachmentError('Nao foi possivel enviar o anexo.');
    }
  }

  function validateAttachment(file: File) {
    const fileName = file.name.toLowerCase();
    const isAllowedImage =
      ALLOWED_ATTACHMENT_TYPES.includes(file.type) ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png');

    if (file.type === 'application/pdf' || fileName.endsWith('.pdf')) {
      return 'PDF sera habilitado em uma proxima etapa.';
    }

    if (!isAllowedImage) {
      return 'Formato invalido. Envie JPG ou PNG.';
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      return 'Arquivo maior que 1 MB.';
    }

    return '';
  }

  function getAttachmentContentType(file: File) {
    const fileName = file.name.toLowerCase();

    if (file.type) return file.type;
    if (fileName.endsWith('.png')) return 'image/png';

    return 'image/jpeg';
  }

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  function onAttachmentClick() {
    fileInputRef.current?.click();
  }

  function onAttachmentSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (file) {
      sendAttachment(file);
    }
  }

  function parseSignalMessage(event: SignalEvent, origin: Roles): IChatMessage {
    const data = event.data || '';
    const messageTime = event.from?.creationTime || new Date();

    try {
      const payload = JSON.parse(data) as AttachmentSignalPayload;

      if (payload.kind === 'attachment' && payload.previewDataUrl) {
        return {
          read: props.isActiveChat,
          origin,
          kind: 'attachment',
          attachment: {
            id: payload.id,
            fileName: payload.fileName,
            contentType: payload.contentType,
            size: payload.size,
            previewDataUrl: payload.previewDataUrl,
          },
          date: messageTime,
        };
      }
    } catch {
      // Keep compatibility with plain text chat payloads.
    }

    return {
      read: props.isActiveChat,
      origin,
      kind: 'text',
      text: data,
      date: messageTime,
    };
  }

  function formatAttachmentSize(size: number) {
    return `${Math.max(size / 1024, 1).toFixed(0)} KB`;
  }

  function renderMessage(message: IChatMessage, key: number) {
    const className = `messageText ${message.origin === Roles.Insured ? 'insuredMessage' : 'analystMessage'}`;

    if (message.kind === 'attachment' && message.attachment) {
      return (
        <div className={className} key={key} ref={messagesEndRef}>
          <img className="attachmentPreview" src={message.attachment.previewDataUrl} alt={message.attachment.fileName} />
          <div className="attachmentName">{message.attachment.fileName}</div>
          <div className="attachmentMeta">{formatAttachmentSize(message.attachment.size)}</div>
          <a className="attachmentDownload" href={message.attachment.previewDataUrl} download={message.attachment.fileName}>
            Baixar
          </a>
        </div>
      );
    }

    return (
      <div className={className} key={key} ref={messagesEndRef}>
        {message.text}
      </div>
    );
  }

  function onInputKeyPress(e: FormEvent<HTMLTextAreaElement>) {
    setInput((e.target as HTMLTextAreaElement).value);
  }

  function scrollIntoView() {
    if (messagesEndRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    scrollIntoView();
  }, [messages]);

  useEffect(() => {
    if (props.isActiveChat) {
      props.setUnreadMessages(false);
    }
  }, [props.isActiveChat]);

  useEffect(() => {
    messages.find((i) => i.origin === Roles.Analyst && i.read === false)
      ? props.setUnreadMessages(true)
      : props.setUnreadMessages(false);
  }, [messages]);

  useEffect(() => {
    props.session.on(`signal:${Signals.MessagingInsured}`, (event: SignalEvent) => {
      const messageFromAnalyst = parseSignalMessage(event, Roles.Analyst);

      setMessages((messages) => [...messages, messageFromAnalyst]);
    });
  }, []);

  return (
    <div className="messageContainer">
      {props.isActiveChat ? (
        <div className="messageBox">
          <div className="messageContent">
            {messages.map(renderMessage)}
          </div>
          <div className="messageControl">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              hidden
              onChange={onAttachmentSelected}
            />
            <textarea typeof="text" value={input} onInput={onInputKeyPress} />
            <Button
              title="Anexar arquivo"
              onClick={onAttachmentClick}
              sx={{
                minWidth: '42px',
                width: '42px',
                margin: 'auto 4px',
                height: '42px',
                color: 'white',
                padding: '8px',
                borderRadius: '8px',
                background: '#555',
              }}
            >
              <AttachFile fontSize="small" />
            </Button>
            <Button
              disabled={!props.isActiveChat}
              // placeholder={'Enviar'}
              onClick={sendMessage}
              sx={{
                gap: '10px',
                width: '66px',
                margin: 'auto',
                height: '42px',
                color: 'white',
                padding: '15px',
                borderRadius: '8px',
                position: 'relative',
                background: '#FF8527',
              }}
            >
              Enviar
            </Button>
          </div>
          {attachmentError ? <div className="attachmentError">{attachmentError}</div> : <></>}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
